
import { useState, useEffect, useRef } from 'react';
import { auth, db, loginWithGoogle } from '../lib/firebase';
import { isAdminUser, getUserEmail } from '../lib/admin';
import { ConsentSwitch } from './ui/ConsentSwitch';
import { signInWithEmailAndPassword, onAuthStateChanged, User } from 'firebase/auth';
import { doc, setDoc, getDoc, onSnapshot } from 'firebase/firestore';
import { ShieldCheck, Activity, LogIn, Mail, Lock, User as UserIcon, Phone, FileText, Key } from 'lucide-react';

enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId?: string | null;
    email?: string | null;
    emailVerified?: boolean | null;
    isAnonymous?: boolean | null;
    tenantId?: string | null;
    providerInfo?: {
      providerId?: string | null;
      email?: string | null;
    }[];
  }
}

function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
      providerInfo: auth.currentUser?.providerData?.map(provider => ({
        providerId: provider.providerId,
        email: provider.email,
      })) || []
    },
    operationType,
    path
  };
  console.error('Firestore Error: ', JSON.stringify(errInfo));
}

// Aba aberta com uma versão antiga do site: o servidor já foi atualizado e a rota que ela chama
// não existe mais (resposta 404 / HTML). Em vez de "Unexpected token '<'", orientamos a recarregar.
const STALE_APP_MESSAGE = 'O FLOWAES foi atualizado. Recarregue a página (no celular, puxe a tela para baixo) e tente de novo.';

// Lê a resposta da API sem quebrar quando ela não é JSON.
async function readApi(res: Response): Promise<{ data: any; stale: boolean }> {
  const isJson = (res.headers.get('content-type') || '').includes('application/json');
  const data = isJson ? await res.json().catch(() => ({})) : {};
  const stale = (res.status === 404 && (!isJson || data?.code === 'API_NOT_FOUND')) || (res.ok && !isJson);
  return { data, stale };
}

/** "4 min 30 s", "1 h", "6 h 5 min": tempo de espera legível. */
const formatWait = (totalSeconds: number): string => {
  const s = Math.max(1, Math.ceil(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return m > 0 ? `${h} h ${m} min` : `${h} h`;
  if (m > 0) return sec > 0 && m < 5 ? `${m} min ${sec} s` : `${m} min`;
  return `${sec} s`;
};
const rateLimitMessage = (what: string, data: any) =>
  data?.retryAfterSeconds
    ? `Você já pediu ${what} para este e-mail. Aguarde ${formatWait(data.retryAfterSeconds)} para pedir novamente.`
    : 'Muitas tentativas. Aguarde alguns minutos e tente novamente.';

// A conta já existe: o código NÃO é enviado e a pessoa segue para o login normal.
/** Cronômetro regressivo: "04:59" (ou "1:00:00" quando passa de 1 hora). */
const formatClock = (totalSeconds: number): string => {
  const s = Math.max(0, Math.ceil(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(sec).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
};

// Código ativo (só o horário de expiração; o código em si fica no servidor).
// Fica em sessionStorage para sobreviver a "voltar" e a recarregar a página.
const CODE_SESSION_KEY = 'flowaes_signup_code';
interface CodeSession { email: string; expiresAt: number; simCode?: string }
const loadCodeSession = (): CodeSession | null => {
  try {
    const raw = sessionStorage.getItem(CODE_SESSION_KEY);
    const v = raw ? JSON.parse(raw) : null;
    return v && typeof v.email === 'string' && typeof v.expiresAt === 'number' && v.expiresAt > Date.now() ? v : null;
  } catch { return null; }
};
const saveCodeSession = (v: CodeSession | null) => {
  try {
    if (v) sessionStorage.setItem(CODE_SESSION_KEY, JSON.stringify(v));
    else sessionStorage.removeItem(CODE_SESSION_KEY);
  } catch { /* sem armazenamento: o fluxo continua funcionando */ }
};

// Bilhete de aprovação (entregue pelo servidor depois do código): guardado para o cadastro
// sobreviver a recarregar a página. Fica em sessionStorage de propósito (software financeiro,
// computadores compartilhados): fechar a aba apaga o bilhete. Vale só para o mesmo e-mail,
// expira junto com o bilhete do servidor (15 min) e só serve para UM cadastro.
const TICKET_SESSION_KEY = 'flowaes_signup_ticket';
interface TicketSession { email: string; ticket: string; expiresAt: number }
const clearTicketSession = () => {
  try { sessionStorage.removeItem(TICKET_SESSION_KEY); } catch { /* sem armazenamento */ }
};
const saveTicketSession = (v: TicketSession) => {
  try { sessionStorage.setItem(TICKET_SESSION_KEY, JSON.stringify(v)); } catch { /* sem armazenamento */ }
};
const loadTicketSession = (): TicketSession | null => {
  try {
    const raw = sessionStorage.getItem(TICKET_SESSION_KEY);
    const v = raw ? JSON.parse(raw) : null;
    if (v && typeof v.email === 'string' && typeof v.ticket === 'string' && v.ticket.length === 64
        && typeof v.expiresAt === 'number' && v.expiresAt > Date.now()) return v;
    if (raw) clearTicketSession();
  } catch { /* ignora */ }
  return null;
};

const ACCOUNT_EXISTS_MESSAGE = 'Esta conta já existe. Digite sua senha para entrar. Se não lembrar, use "Esqueci minha senha" abaixo.';

export default function OnboardingPortal({ onAccessGranted }: { onAccessGranted: () => void }) {
  const onAccessGrantedRef = useRef(onAccessGranted);
  useEffect(() => {
    onAccessGrantedRef.current = onAccessGranted;
  }, [onAccessGranted]);

  useEffect(() => {
    if (isAdminUser(auth.currentUser)) {
      onAccessGrantedRef.current();
    }
  }, []);

  // Bilhete guardado e ainda válido: o cadastro volta direto para a etapa de criar a senha
  const [ticketBoot] = useState<TicketSession | null>(loadTicketSession);
  const [step, setStep] = useState(ticketBoot ? 6 : 0); // 0: Landing, 1: Auth, 2: Questionnaire, 3: NDA, 4: Waitlist, 5: Security Panel
  const [isLoginMode, setIsLoginMode] = useState(false);
  
  // Auth Form State
  const [email, setEmail] = useState(ticketBoot?.email || '');
  const [password, setPassword] = useState('');
  const [formData, setFormData] = useState({ cpf: '', name: '', nickname: '', phone: '', capital: '', experience: '', reason: '' });
  const [formStep, setFormStep] = useState(1);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  // Opt-in explícito para comunicações de marketing (FLOWAES Intelligence / Brevo)
  const [optInMarketing, setOptInMarketing] = useState(false);
  const [infoMessage, setInfoMessage] = useState('');
  const [isNdaAccepted, setIsNdaAccepted] = useState(false);
  
  const [userUid, setUserUid] = useState('');
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const isSigningUpRef = useRef(false);
  const adminSetDocAttemptedRef = useRef(false);
  const autoLinkAttemptedRef = useRef<string | null>(null);

  const [activationKey, setActivationKey] = useState('');

  // Email Verification Code Flow State
  const [verificationCode, setVerificationCode] = useState('');
  // O código em si fica só no servidor. "isCodeVerified" + "signupTicket" provam que o
  // e-mail foi confirmado; sem eles a tela de senha NUNCA é exibida (e o servidor
  // também recusa criar a conta sem o bilhete).
  const [isCodeVerified, setIsCodeVerified] = useState(!!ticketBoot);
  const [signupTicket, setSignupTicket] = useState(ticketBoot?.ticket || '');
  const [verificationType, setVerificationType] = useState<'signup' | null>(null);
  const [pendingEmail, setPendingEmail] = useState(ticketBoot?.email || '');
  const [simulationCodeBanner, setSimulationCodeBanner] = useState('');
  // Código ativo deste e-mail: sobrevive ao "Cancelar/voltar" para reabrir a tela sem pedir outro
  const [codeSession, setCodeSessionState] = useState<CodeSession | null>(() => loadCodeSession());
  const [nowMs, setNowMs] = useState(() => Date.now());
  const setCodeSession = (v: CodeSession | null) => { setCodeSessionState(v); saveCodeSession(v); };
  const codeSecondsLeft = codeSession ? Math.max(0, Math.ceil((codeSession.expiresAt - nowMs) / 1000)) : 0;

  // Relógio do cronômetro (só roda enquanto a tela do código está aberta)
  useEffect(() => {
    if (verificationType === null) return;
    setNowMs(Date.now());
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [verificationType]);

  const [pin, setPin] = useState(['', '', '', '']);
  const [pinError, setPinError] = useState('');

  // Reinicia o cadastro do começo (tela do e-mail), apagando qualquer prova de verificação.
  const resetSignupFlow = (notice = '') => {
    setVerificationType(null);
    setVerificationCode('');
    setSimulationCodeBanner('');
    setIsCodeVerified(false);
    setSignupTicket('');
    setPendingEmail('');
    setPassword('');
    setPasswordConfirm('');
    setIsRobotVerified(false);
    setIsLoginMode(false);
    setError('');
    setInfoMessage(notice);
    setStep(1);
  };

  // Guarda de rota: a tela de criar senha só existe para quem confirmou o código.
  const canCreatePassword = isCodeVerified && !!signupTicket && !!pendingEmail;
  const inSignupFlow = verificationType === 'signup' || (step === 6 && canCreatePassword);

  useEffect(() => {
    if (step === 6 && !canCreatePassword) {
      resetSignupFlow('Confirme o seu e-mail para continuar o cadastro.');
    }
  }, [step, canCreatePassword]);

  // Botão/gesto de "voltar" durante o cadastro: volta ao começo em vez de pular etapas.
  useEffect(() => {
    if (!inSignupFlow) return;
    window.history.pushState({ flowaesSignup: true }, '');
    const onPopState = () => resetSignupFlow('Você voltou ao início do cadastro. Confirme o seu e-mail para continuar.');
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [inSignupFlow]);

  // O servidor ainda reconhece o bilhete? (false só quando ele diz que não; falha de rede = tenta seguir)
  const isTicketStillValid = async (targetEmail: string, ticket: string): Promise<boolean> => {
    try {
      const res = await fetch('/api/signup/check-ticket', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: targetEmail, ticket })
      });
      const { data, stale } = await readApi(res);
      if (stale || !res.ok) return true;
      return data.valid !== false;
    } catch {
      return true; // o servidor confere de novo na hora de criar a conta
    }
  };

  // Recarregou a página (ou voltou à aba) com um bilhete guardado: confirma com o servidor.
  useEffect(() => {
    if (!ticketBoot) return;
    let cancelled = false;
    (async () => {
      if (!(await isTicketStillValid(ticketBoot.email, ticketBoot.ticket)) && !cancelled) {
        clearTicketSession();
        resetSignupFlow('Sua verificação expirou. Confirme o seu e-mail novamente.');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleVerifyCode = async () => {
    setError('');
    if (verificationCode.length !== 6) {
      setError('O código deve conter 6 dígitos.');
      return;
    }
    setIsLoading(true);
    try {
      const verifyRes = await fetch('/api/signup/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: pendingEmail, code: verificationCode })
      });
      const { data: verifyData, stale: verifyStale } = await readApi(verifyRes);
      if (verifyStale) {
        setError(STALE_APP_MESSAGE);
        return;
      }
      if (!verifyRes.ok || !verifyData.ticket) {
        setError(verifyData.error || 'Código de verificação incorreto.');
        return;
      }
      // Só aqui a tela de senha é liberada
      setSignupTicket(verifyData.ticket);
      saveTicketSession({
        email: pendingEmail,
        ticket: verifyData.ticket,
        expiresAt: Date.now() + (Number(verifyData.ticketExpiresInSeconds) > 0 ? Number(verifyData.ticketExpiresInSeconds) : 15 * 60) * 1000,
      });
      setCodeSession(null);
      setIsCodeVerified(true);
      setVerificationType(null);
      setVerificationCode('');
      setSimulationCodeBanner('');
      setStep(6);
    } catch (e: any) {
      setError('Não foi possível confirmar o código agora. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleActivateKey = async () => {
    setError('');
    setIsLoading(true);
    try {
      const trimmedKey = activationKey.trim().toUpperCase();
      if (!trimmedKey) {
        setError('Por favor, digite uma chave de acesso.');
        setIsLoading(false);
        return;
      }

      let isKeyValid = false;
      let licenseDocRef = null;

      const isAdminEmail = isAdminUser(auth.currentUser);

      if (isAdminEmail) {
          onAccessGranted();
        setIsLoading(false);
        return;
      }

      // Consulta a licença no Firebase (não existem mais chaves fixas no código)
      {
        const licSnap = await getDoc(doc(db, 'licenses', trimmedKey));
        if (licSnap.exists()) {
          const licData = licSnap.data();
          if (licData.status === 'active') {
            isKeyValid = true;
            licenseDocRef = doc(db, 'licenses', trimmedKey);
          } else {
            setError('Esta chave de licença já foi utilizada.');
            setIsLoading(false);
            return;
          }
        }
      }

      if (isKeyValid) {
        const userRef = doc(db, 'users', userUid);
        const userSnap = await getDoc(userRef);
        const existingActivities = userSnap.exists() ? (userSnap.data().activities || []) : [];
        const newActivity = {
          type: 'key_activation',
          description: `Acesso ativado usando a chave de licença: ${trimmedKey}`,
          timestamp: new Date().toISOString(),
          by: 'Usuário'
        };

        // If it was a dynamic license from the db, mark it as used
        if (licenseDocRef) {
          await setDoc(licenseDocRef, {
            status: 'used',
            usedBy: currentUser?.email || email || '',
            usedAt: new Date().toISOString()
          }, { merge: true });
        }

        // Save activation state in user document
        const updateObj: any = { 
          subscription_status: 'active',
          license_key: trimmedKey,
          activities: [newActivity, ...existingActivities]
        };
        if (!userSnap.exists()) {
          updateObj.createdAt = new Date().toISOString();
          updateObj.email = currentUser?.email || email || '';
          updateObj.uid = userUid;
        }
        await setDoc(userRef, updateObj, { merge: true });

        // If they haven't completed their profile, go to questionnaire (step 2) to finish profile
        const userHasCpf = userSnap.exists() && userSnap.data().cpf;
        if (!userHasCpf) {
          setStep(2); // Go to questionnaire to finish profile
        } else {
          // Already has profile, go to NDA
          setStep(3);
        }
      } else {
        setError('Chave inválida ou já utilizada.');
      }
    } catch (e: any) {
      setError('Erro ao ativar licença: ' + e.message);
      handleFirestoreError(e, OperationType.WRITE, `users/${userUid}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePinChange = (index: number, val: string) => {
    if (!/^\d*$/.test(val)) return;
    const newPin = [...pin];
    newPin[index] = val.slice(-1);
    setPin(newPin);
    setPinError('');

    if (val && index < 3) {
      const nextInput = document.getElementById(`pin-input-${index + 1}`);
      if (nextInput) (nextInput as HTMLInputElement).focus();
    }
  };

  const handlePinKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      const prevInput = document.getElementById(`pin-input-${index - 1}`);
      if (prevInput) {
        (prevInput as HTMLInputElement).focus();
        const newPin = [...pin];
        newPin[index - 1] = '';
        setPin(newPin);
      }
    }
  };

  const handleVerifyPin = async () => {
    const enteredPin = pin.join('');
    if (enteredPin.length !== 4) {
      setPinError('Insira o PIN de 4 dígitos.');
      return;
    }

    // Só o admin logado com a própria conta pode desbloquear (sem PIN fixo no código)
    if (isAdminUser(auth.currentUser)) {
      setIsLoading(true);
      try {
        const uidToUse = userUid || currentUser?.uid;
        if (!uidToUse) {
          throw new Error("Usuário não autenticado.");
        }
        const userRef = doc(db, 'users', uidToUse);
        await setDoc(userRef, {
          subscription_status: 'active',
          nda_accepted: true,
          unblockedAt: new Date().toISOString()
        }, { merge: true });

        onAccessGrantedRef.current();
        setError('');
        setPin(['', '', '', '']);
        setStep(0);
      } catch (e: any) {
        setPinError('Erro ao desbloquear no Firebase: ' + e.message);
      } finally {
        setIsLoading(false);
      }
    } else {
      setPinError('PIN incorreto. Acesso GOD negado.');
    }
  };

  useEffect(() => {
    let userDocUnsub: (() => void) | null = null;

    const unsub = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      setIsLoading(false); // Instantly clear any pending auth loading states on state change
      if (userDocUnsub) {
        userDocUnsub();
        userDocUnsub = null;
      }

      if (user) {
        setUserUid(user.uid);
        if (user.email) {
          setEmail(user.email);
        }
        
        // Skip DB check if we are in the middle of email signup (we'll handle it manually)
        if (isSigningUpRef.current) return;
        
        const docRef = doc(db, 'users', user.uid);
        
        // Auto-link/migrate user by email if UID doc doesn't exist
        if (getUserEmail(user) && autoLinkAttemptedRef.current !== user.uid) {
          autoLinkAttemptedRef.current = user.uid;
          const emailLower = getUserEmail(user);
          const checkAndLink = async () => {
            try {
              const { getDoc, collection, query, where, getDocs, setDoc } = await import('firebase/firestore');
              const snap = await getDoc(docRef);
              if (!snap.exists()) {
                const q = query(collection(db, 'users'), where('email', '==', emailLower));
                const querySnap = await getDocs(q);
                if (!querySnap.empty) {
                  const existingDoc = querySnap.docs[0];
                  const existingData = existingDoc.data();
                  console.log("[Onboarding] Migrating/linking existing user document to new UID", user.uid, existingData);
                  await setDoc(docRef, {
                    ...existingData,
                    uid: user.uid,
                  }, { merge: true });
                }
              }
            } catch (e) {
              console.warn("[Onboarding] Error during auto-link (proceeding with local auth):", e);
            }
          };
          checkAndLink();
        }

        userDocUnsub = onSnapshot(docRef, (docSnap) => {
          setIsLoading(false);
          const data = docSnap.exists() ? docSnap.data() : null;

          const isAdmin = isAdminUser(user);
          
          if (isAdmin) {
             if (!adminSetDocAttemptedRef.current && (!docSnap.exists() || docSnap.data()?.subscription_status === 'blocked' || !docSnap.data()?.cpf || docSnap.data()?.role !== 'admin')) {
                adminSetDocAttemptedRef.current = true;
                const docRef = doc(db, 'users', user.uid);
                setDoc(docRef, {
                   email: getUserEmail(user),
                   role: 'admin',
                   subscription_status: 'active',
                   nda_accepted: true,
                   name: user.displayName || 'Creator God',
                   cpf: '000.000.000-00'
                }, { merge: true }).catch(e => console.warn('Falha ao atualizar o cadastro do admin:', e));
             }
                    onAccessGrantedRef.current();
             return;
          }

          if (data && data.subscription_status === 'blocked') {
             setError('Sua conta está bloqueada por questões de segurança. Contate o suporte.');
             setStep(4); // Waitlist/Blocked screen
             return;
          }

          const hasCompletedQualification = 
            !!data?.qualification_submitted || 
            !!data?.onboarding_completed || 
            !!data?.qualification || 
            !!data?.cpf || 
            localStorage.getItem(`flowaes_qualification_${user.uid}`) === 'true';

          if (!hasCompletedQualification) {
             // Direct to Step 2 (Multi-step Profile form)
             setStep(2); 
             setFormData(prev => ({
               ...prev,
               name: user.displayName || (docSnap.exists() ? docSnap.data()?.name : '') || prev.name || '',
               nickname: (docSnap.exists() ? docSnap.data()?.nickname : '') || prev.nickname || '',
               phone: (docSnap.exists() ? docSnap.data()?.phone : '') || prev.phone || '',
               cpf: (docSnap.exists() ? docSnap.data()?.cpf : '') || prev.cpf || '',
               capital: (docSnap.exists() ? docSnap.data()?.qualification?.capital : '') || prev.capital || '',
               experience: (docSnap.exists() ? docSnap.data()?.qualification?.experience : '') || prev.experience || '',
               reason: (docSnap.exists() ? docSnap.data()?.qualification?.reason : '') || prev.reason || '',
             }));
          } else {
             const isAdmin = isAdminUser(user);
             const isActive = data?.subscription_status === 'active' || isAdmin;
             
             if (isActive) {
               if (data?.nda_accepted || isAdmin) {
                 onAccessGrantedRef.current();
               } else {
                 setStep(3); // Go to NDA
               }
             } else if (data?.subscription_status === 'blocked') {
               setError('Sua conta está bloqueada por questões de segurança. Contate o suporte.');
               setStep(4); // Waitlist/Blocked screen
             } else {
               setStep(4); // Waitlist
             }
          }
        }, (e) => {
          console.error("Error reading user data", e);
          setIsLoading(false);
          if (isAdminUser(user)) {
             onAccessGrantedRef.current();
          } else {
             handleFirestoreError(e, OperationType.GET, `users/${user.uid}`);
          }
        });
      } else {
        setUserUid('');
        setEmail('');
        setFormData({ cpf: '', name: '', nickname: '', phone: '', capital: '', experience: '', reason: '' });
        setStep(prevStep => prevStep > 1 && prevStep !== 6 ? 0 : prevStep);
      }
    });
    return () => {
      unsub();
      if (userDocUnsub) userDocUnsub();
    };
  }, []);

  const handleSaveProfile = async () => {
    if (!formData.cpf || !formData.phone || !isTermsAccepted || !formData.name || !formData.capital || !formData.experience || !formData.reason) {
      setError('Por favor, preencha todos os campos e aceite os termos para prosseguir.');
      return;
    }
    setError('');

    setIsLoading(true);
    try {
      const userRef = doc(db, 'users', userUid);
      
      // Fetch current document to preserve subscription_status
      const currentSnap = await getDoc(userRef);
      const currentData = currentSnap.data();
      const existingStatus = currentData?.subscription_status || 'pending';
      const existingActivities = currentData?.activities || [];

      const newActivity = {
        type: 'profile_update',
        description: 'Dados de qualificação e perfil preenchidos/atualizados.',
        timestamp: new Date().toISOString(),
        by: 'Usuário'
      };

      await setDoc(userRef, {
        uid: userUid,
        email: getUserEmail(auth.currentUser) || email,
        name: formData.name,
        nickname: formData.nickname,
        phone: formData.phone,
        cpf: formData.cpf,
        qualification: { capital: formData.capital, experience: formData.experience, reason: formData.reason },
        qualification_submitted: true,
        onboarding_completed: true,
        terms_accepted: isTermsAccepted,
        optInMarketing: optInMarketing,
        marketing_accepted: optInMarketing,
        subscription_status: existingStatus,
        activities: [newActivity, ...existingActivities],
        createdAt: currentData?.createdAt || new Date().toISOString()
      }, { merge: true });

      // Inclui na lista da FLOWAES Intelligence (Brevo) somente com opt-in explícito
      if (optInMarketing) {
        try {
          const idToken = await auth.currentUser?.getIdToken();
          if (idToken) {
            await fetch('/api/marketing/optin', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
              body: JSON.stringify({ name: formData.name })
            });
          }
        } catch (optErr) {
          console.warn('Falha ao registrar opt-in de marketing (cadastro segue normalmente):', optErr);
        }
      }

      // E-mail de boas-vindas (enviado pelo servidor, uma única vez por conta)
      if (!currentData?.welcome_email_sent && !isAdminUser(auth.currentUser)) {
        try {
          const idToken = await auth.currentUser?.getIdToken();
          if (idToken) {
            const welcomeRes = await fetch('/api/email/welcome', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
              body: JSON.stringify({ name: formData.name })
            });
            if (welcomeRes.ok) {
              await setDoc(userRef, { welcome_email_sent: true }, { merge: true });
            }
          }
        } catch (welcomeErr) {
          console.warn('Falha ao enviar o e-mail de boas-vindas (cadastro segue normalmente):', welcomeErr);
        }
      }

      localStorage.setItem(`flowaes_qualification_${userUid}`, 'true');

      const docSnap = await getDoc(userRef);
      const data = docSnap.data();
      const isAdmin = isAdminUser(auth.currentUser);
      const isActive = data?.subscription_status === 'active' || isAdmin;
      
      
      if (isActive) {
        if (data?.nda_accepted || isAdmin) {
          onAccessGrantedRef.current();
        } else {
          setStep(3);
        }
      } else {
        setStep(4);
      }
    } catch (e: any) {
      setError('Erro ao salvar dados: ' + e.message);
      handleFirestoreError(e, OperationType.WRITE, `users/${userUid}`);
    } finally {
      setIsLoading(false);
    }
  };

  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [isRobotVerified, setIsRobotVerified] = useState(false);

  // Abre a tela do código com o cronômetro apontando para o instante de expiração.
  const openCodeScreen = (targetEmail: string, expiresAt: number, simCode?: string) => {
    setCodeSession({ email: targetEmail, expiresAt, simCode });
    setNowMs(Date.now());
    setInfoMessage('');
    setPendingEmail(targetEmail);
    setIsCodeVerified(false);
    setSignupTicket('');
    setVerificationCode('');
    setSimulationCodeBanner(simCode || '');
    setVerificationType('signup'); // mostra a tela do código; NÃO vai para a senha
  };

  // Pede o código ao servidor. O servidor confere no Firebase Auth se a conta já existe ANTES de enviar.
  const requestSignupCode = async (targetEmail: string) => {
    const startRes = await fetch('/api/signup/start', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: targetEmail })
    });
    const { data: startData, stale: startStale } = await readApi(startRes);
    if (startStale) {
      setError(STALE_APP_MESSAGE);
    } else if (startRes.status === 400) {
      setError('E-mail inválido.');
    } else if (startRes.status === 409 && startData.error === 'account_exists') {
      // Conta já existe: o servidor não enviou código. Vai direto ao login (senha + "Esqueci minha senha")
      setCodeSession(null);
      setIsLoginMode(true);
      setPassword('');
      setVerificationType(null);
      setInfoMessage(ACCOUNT_EXISTS_MESSAGE);
    } else if (startRes.status === 429 && startData.code === 'rate_limited' && startData.retryAfterSeconds) {
      // Já há um código ativo para este e-mail (o servidor não gera outro enquanto a espera dura)
      openCodeScreen(targetEmail, Date.now() + startData.retryAfterSeconds * 1000, codeSession?.email === targetEmail ? codeSession.simCode : undefined);
      setError('');
    } else if (startRes.status === 429) {
      setError(rateLimitMessage('um código', startData));
    } else if (!startRes.ok) {
      setError(startData.error || 'Cadastro indisponível no momento. Tente novamente em instantes.');
    } else if (startData.status === 'code-sent') {
      const ttl = Number(startData.expiresInSeconds) > 0 ? Number(startData.expiresInSeconds) : 5 * 60;
      openCodeScreen(targetEmail, Date.now() + ttl * 1000, startData.method === 'simulation' && startData.code ? startData.code : undefined);
    } else {
      setError('Resposta inesperada do servidor. Tente novamente.');
    }
  };

  // Botão "Reenviar código" (liberado só quando o código anterior expirou)
  const handleResendCode = async () => {
    if (codeSecondsLeft > 0 || !pendingEmail) return;
    setError('');
    setIsLoading(true);
    try {
      await requestSignupCode(pendingEmail);
    } catch (err: any) {
      setError('Não foi possível reenviar agora. Verifique sua conexão e tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailAuth = async () => {
    try {
      setError('');
      setIsLoading(true);
      const trimmedEmail = email.trim().toLowerCase();
      setEmail(trimmedEmail);
      if (isLoginMode) {
        if (!trimmedEmail || !password) {
          setError('Preencha email e senha.');
          setIsLoading(false);
          return;
        }
        await signInWithEmailAndPassword(auth, trimmedEmail, password);
        // onAuthStateChanged will handle the rest for login
      } else {
        if (!trimmedEmail) {
          setError('Preencha o email.');
          setIsLoading(false);
          return;
        }
        
        // E-mail já confirmado há pouco (bilhete guardado e válido): segue direto para criar a senha
        const savedTicket = loadTicketSession();
        if (savedTicket && savedTicket.email === trimmedEmail) {
          if (await isTicketStillValid(trimmedEmail, savedTicket.ticket)) {
            setCodeSession(null);
            setInfoMessage('');
            setPendingEmail(trimmedEmail);
            setSignupTicket(savedTicket.ticket);
            setIsCodeVerified(true);
            setVerificationType(null);
            setPassword('');
            setPasswordConfirm('');
            setStep(6);
            return;
          }
          clearTicketSession();
        }

        // Já existe um código ativo para este mesmo e-mail (ex.: a pessoa voltou): reabre a tela
        // do código SEM pedir outro ao servidor.
        if (codeSession && codeSession.email === trimmedEmail && codeSession.expiresAt > Date.now()) {
          openCodeScreen(trimmedEmail, codeSession.expiresAt, codeSession.simCode);
        } else {
          await requestSignupCode(trimmedEmail);
        }
      }
    } catch (err: any) {
      setError('Erro na autenticação: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
    const targetEmail = email.trim().toLowerCase();
    if (!targetEmail) {
      setError('Preencha o email para recuperar a senha.');
      return;
    }
    setError('');
    setInfoMessage('');
    setIsLoading(true);
    try {
      // O servidor gera o link seguro e envia o e-mail do FLOWAES (flowaes.com/auth/action).
      const response = await fetch('/api/auth/password-reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: targetEmail })
      });
      const { data: resetData, stale: resetStale } = await readApi(response);
      if (resetStale) {
        setError(STALE_APP_MESSAGE);
      } else if (response.status === 400) {
        setError('E-mail inválido.');
      } else if (response.status === 403 && resetData.error === 'email_not_verified') {
        setError('Esta conta ainda não teve o e-mail confirmado. Confirme primeiro o e-mail cadastrado e só depois redefina a senha.');
      } else if (response.status === 429) {
        setError(rateLimitMessage('a redefinição', resetData));
      } else if (response.status === 503) {
        setError('Recuperação de senha indisponível no momento. Contate support@flowaes.com.');
      } else {
        // Mesma mensagem exista a conta ou não (não revela quem está cadastrado)
        setInfoMessage('Se este e-mail estiver cadastrado, você receberá um link para redefinir a senha. Confira também a caixa de spam.');
      }
    } catch (err: any) {
      setError('Não foi possível enviar agora. Verifique sua conexão e tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFinalizeSignup = async () => {
    // Guarda: sem e-mail confirmado não há criação de senha
    if (!canCreatePassword) {
      resetSignupFlow('Confirme o seu e-mail para continuar o cadastro.');
      return;
    }
    const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+{}\[\]:;<>,.?~\\/-]).{8,}$/;
    if (!passwordRegex.test(password)) {
      setError('A senha deve ser forte: mínimo de 8 caracteres, 1 letra maiúscula, 1 número e 1 caractere especial.');
      return;
    }
    if (password !== passwordConfirm) {
      setError('As senhas não coincidem.');
      return;
    }
    if (!isRobotVerified) {
      setError('Por favor, confirme que não é um robô.');
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      // A conta é criada PELO SERVIDOR, e só com o bilhete do código confirmado.
      const createRes = await fetch('/api/signup/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: pendingEmail, password, ticket: signupTicket })
      });
      const { data: createData, stale: createStale } = await readApi(createRes);
      if (createStale) {
        setError(STALE_APP_MESSAGE);
        return;
      }
      if (!createRes.ok) {
        if (createData.reason === 'ticket') {
          clearTicketSession();
          resetSignupFlow('Sua verificação expirou. Confirme o seu e-mail novamente.');
        } else if (createData.reason === 'exists') {
          clearTicketSession();
          const existing = pendingEmail;
          resetSignupFlow('');
          setEmail(existing);
          setIsLoginMode(true);
          setInfoMessage(ACCOUNT_EXISTS_MESSAGE);
        } else {
          setError(createData.error || 'Não foi possível criar a conta agora. Tente novamente.');
        }
        return;
      }

      // Conta criada: o bilhete foi consumido no servidor; apaga a cópia do navegador
      clearTicketSession();
      // entra com a senha escolhida e cria o cadastro pendente
      isSigningUpRef.current = true;
      const userCred = await signInWithEmailAndPassword(auth, pendingEmail, password);
      const uid = userCred.user.uid;
      await setDoc(doc(db, 'users', uid), {
        uid: uid,
        email: pendingEmail,
        subscription_status: 'pending',
        createdAt: new Date().toISOString()
      });
      isSigningUpRef.current = false;

      setVerificationType(null);
      setVerificationCode('');
      setSimulationCodeBanner('');
      setIsCodeVerified(false);
      setSignupTicket('');
      setPassword('');
      setPasswordConfirm('');
      setIsRobotVerified(false);
      setStep(2); // Segue para o formulário de qualificação
    } catch (e: any) {
      isSigningUpRef.current = false;
      setError('Erro ao concluir cadastro: ' + (e?.message || 'tente novamente.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setError('');
      setIsLoading(true);
      await loginWithGoogle();
      // onAuthStateChanged will handle the rest
    } catch (err: any) {
      setError('Erro com Google: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const acceptNda = async () => {
    try {
      setIsLoading(true);
      const userRef = doc(db, 'users', userUid);
      const userSnap = await getDoc(userRef);
      const existingActivities = userSnap.exists() ? (userSnap.data().activities || []) : [];
      const newActivity = {
        type: 'nda_accept',
        description: 'Termo de Sigilo & NDA assinado e aceito.',
        timestamp: new Date().toISOString(),
        by: 'Usuário'
      };
      await setDoc(userRef, { 
        nda_accepted: true, 
        nda_accepted_at: new Date().toISOString(),
        activities: [newActivity, ...existingActivities]
      }, { merge: true });
      onAccessGrantedRef.current();
    } catch (e: any) {
      setError('Erro ao aceitar NDA: ' + e.message);
      setIsLoading(false);
      handleFirestoreError(e, OperationType.WRITE, `users/${userUid}`);
    }
  };

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-[#02040a] p-4 lg:p-8 font-sans text-white overflow-y-auto">
      <div className={`w-full border border-teal-500/10 bg-[#0a0e27]/95 backdrop-blur-xl rounded-xl shadow-[0_0_50px_rgba(0,0,0,0.8)] overflow-y-auto md:overflow-hidden flex flex-col min-h-[400px] max-h-[95vh] md:max-h-[800px] ${step === 0 ? 'max-w-4xl' : 'max-w-md'}`}>
        
        {step === 0 && (
          <div className="w-full bg-gradient-to-br from-teal-900/40 to-black p-8 md:p-16 flex flex-col justify-center items-center text-center min-h-[500px]">
            <ShieldCheck className="w-16 h-16 text-teal-400 mb-6 opacity-80" />
            <h1 className="text-3xl md:text-5xl font-black mb-6 tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-emerald-200">
              Você não chegou aqui por acaso.
            </h1>
            <button 
              onClick={() => setStep(1)}
              className="bg-teal-500 hover:bg-teal-400 text-black font-bold py-4 px-12 rounded-full tracking-[0.2em] transition-all transform hover:scale-105 shadow-[0_0_20px_rgba(20,184,166,0.3)]"
            >
              ENTRAR
            </button>
          </div>
        )}

        {step !== 0 && (
          <div className="flex-1 w-full bg-[#050714] flex flex-col p-6 md:p-8 overflow-y-auto relative">
            {isLoading && (
              <div className="absolute inset-0 z-50 bg-[#050714]/80 backdrop-blur-sm flex items-center justify-center">
                <Activity className="w-8 h-8 text-teal-500 animate-spin" />
              </div>
            )}

            {infoMessage && !error && (
              <div className="w-full max-w-sm mx-auto mb-6 bg-teal-900/20 border border-teal-500/30 text-teal-300 p-3 rounded text-xs text-center font-mono tracking-wider">
                {infoMessage}
              </div>
            )}

            {error && !(step === 4 && error.includes('bloqueada')) && (
              <div className="w-full max-w-sm mx-auto mb-6 bg-red-900/20 border border-red-500/30 text-red-400 p-3 rounded text-xs text-center font-mono tracking-wider">
                {error}
              </div>
            )}

            <div className="flex-1 flex flex-col justify-center">
            {verificationType !== null ? (
              <div className="space-y-6 w-full max-w-sm mx-auto text-center animate-in fade-in duration-300 font-sans">
                <div className="w-16 h-16 text-teal-400 mx-auto mb-4 flex items-center justify-center bg-teal-500/10 rounded-full border border-teal-500/30 shadow-[0_0_15px_rgba(20,184,166,0.15)]">
                  <Mail size={32} />
                </div>
                <div>
                  <h2 className="text-xl font-bold tracking-widest text-teal-400 uppercase">
                    VERIFICAÇÃO DE PROTOCOLO
                  </h2>
                  <p className="text-[10px] text-white/50 mt-1 font-mono uppercase">
                    Código enviado para: <span className="text-teal-400 font-bold">{pendingEmail}</span>
                  </p>
                </div>

                {simulationCodeBanner && (
                  <div className="p-3 bg-teal-950/40 border border-teal-500/30 rounded text-teal-400 text-xs font-mono tracking-wide text-left space-y-1">
                    <p className="font-bold uppercase text-[9px] tracking-wider text-teal-300 flex items-center gap-1">
                      <span>⚡</span> Modo Simulação Ativo (Sem Brevo)
                    </p>
                    <p className="text-[10px]">Como a chave do Brevo não está configurada neste ambiente de teste, use o código abaixo:</p>
                    <p className="text-center font-bold text-lg tracking-[5px] mt-2 bg-teal-500/10 p-1.5 rounded select-all">{simulationCodeBanner}</p>
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <label className="block text-[10px] uppercase font-bold tracking-[2px] text-teal-500 mb-2 text-left">Código de Confirmação</label>
                    <input 
                      type="text" 
                      placeholder="6 DÍGITOS" 
                      maxLength={6}
                      value={verificationCode} 
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                      className="w-full border border-teal-500/20 bg-[#0a0e27]/50 p-3 rounded text-center text-lg font-bold tracking-[8px] focus:outline-none focus:border-teal-400 font-mono text-white placeholder:tracking-normal placeholder:font-normal placeholder:text-sm animate-pulse"
                    />
                  </div>

                  <div className="flex items-center justify-between gap-3 text-left">
                    <button
                      type="button"
                      onClick={handleResendCode}
                      disabled={codeSecondsLeft > 0 || isLoading}
                      className="text-[10px] font-bold tracking-widest uppercase px-3 py-2 rounded border border-teal-500/30 text-teal-400 transition enabled:hover:bg-teal-500/10 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Reenviar código
                    </button>
                    <span className="text-[11px] font-mono text-white/60" aria-live="off">
                      {codeSecondsLeft > 0 ? (
                        <>Novo código em <span data-testid="resend-timer" className="text-teal-400 font-bold">{formatClock(codeSecondsLeft)}</span></>
                      ) : (
                        <span className="text-teal-400">Você já pode reenviar o código.</span>
                      )}
                    </span>
                  </div>

                  <div className="flex gap-3">
                    <button 
                      onClick={() => resetSignupFlow()}
                      className="flex-1 bg-white/5 hover:bg-white/10 border border-white/10 p-3 rounded font-bold text-[10px] tracking-widest text-white transition uppercase"
                    >
                      Cancelar
                    </button>
                    <button 
                      onClick={handleVerifyCode} 
                      className="flex-1 bg-teal-500 hover:bg-teal-400 p-3 rounded font-bold text-[10px] tracking-widest text-black transition uppercase shadow-[0_0_15px_rgba(20,184,166,0.2)]"
                    >
                      Confirmar
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <>
              {step === 1 && (
              <div className="space-y-6 w-full max-w-sm mx-auto">
                <div className="text-center mb-8">
                  <h2 className="text-xl font-bold tracking-widest text-teal-400 flex items-center justify-center gap-2">
                    {isLoginMode ? <LogIn size={20}/> : <UserIcon size={20}/>}
                    {isLoginMode ? 'LOGIN RESTRITO' : 'CRIAR CONTA'}
                  </h2>
                  <p className="text-xs text-white/50 mt-2 font-mono">
                    Identifique-se para acessar o sistema.
                  </p>
                </div>
                
                <button 
                  onClick={handleGoogleLogin}
                  disabled={isLoading}
                  className="w-full bg-white text-black p-3 rounded font-bold text-sm tracking-widest flex items-center justify-center space-x-2 hover:bg-gray-200 transition disabled:opacity-50"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  <span>CONTINUAR COM GOOGLE</span>
                </button>

                <div className="flex items-center space-x-2 text-white/30 text-xs font-mono">
                  <div className="flex-1 border-t border-white/10"></div>
                  <span>OU E-MAIL</span>
                  <div className="flex-1 border-t border-white/10"></div>
                </div>
                  
                <div className="space-y-4">
                  <div>
                    <label className="text-[10px] uppercase font-bold tracking-[2px] text-teal-500 mb-1 flex items-center gap-1"><Mail size={10}/> Email</label>
                    <input 
                      type="email" 
                      placeholder="SEU EMAIL" 
                      value={email} 
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full border border-teal-500/20 bg-[#0a0e27]/50 p-3 rounded text-sm tracking-wider focus:outline-none focus:border-teal-400 font-mono"
                    />
                  </div>

                  {isLoginMode && (
                    <div>
                      <label className="text-[10px] uppercase font-bold tracking-[2px] text-teal-500 mb-1 flex items-center gap-1"><Key size={10}/> Senha</label>
                      <input 
                        type="password" 
                        placeholder="SUA SENHA" 
                        value={password} 
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full border border-teal-500/20 bg-[#0a0e27]/50 p-3 rounded text-sm tracking-wider focus:outline-none focus:border-teal-400 font-mono"
                      />
                    </div>
                  )}
                  
                  <button 
                    onClick={handleEmailAuth} 
                    disabled={isLoading}
                    className="w-full bg-teal-500 p-3 rounded font-bold text-black text-sm tracking-widest hover:bg-teal-400 transition shadow-[0_0_15px_rgba(20,184,166,0.3)] disabled:opacity-50"
                  >
                    {isLoginMode ? 'ENTRAR' : 'CRIAR CONTA'}
                  </button>
                  
                  {isLoginMode && (
                    <div className="text-center mt-2">
                      <button 
                        onClick={handleResetPassword}
                        className="text-[9px] text-white/30 hover:text-white/70 transition uppercase tracking-[1px]"
                      >
                        Esqueci minha senha
                      </button>
                    </div>
                  )}
                </div>

                <div className="text-center mt-4">
                  <button 
                    onClick={() => setIsLoginMode(!isLoginMode)}
                    disabled={isLoading}
                    className="text-[10px] text-white/50 hover:text-teal-400 tracking-[2px] transition uppercase font-bold"
                  >
                    {isLoginMode ? 'Não tem conta? Criar conta.' : 'Já possui conta? Fazer login.'}
                  </button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4 w-full max-w-sm mx-auto animate-in fade-in zoom-in-95 duration-300">
                <div className="text-center mb-6">
                  <h2 className="text-xl font-bold tracking-widest text-teal-400 flex items-center justify-center gap-2 uppercase">
                    <FileText size={20}/>
                    Formulário de Qualificação
                  </h2>
                  <p className="text-[10px] text-white/50 mt-1 font-mono uppercase">
                    Etapa {formStep} de 3
                  </p>
                  
                  {/* Progress Bar */}
                  <div className="w-full bg-white/10 h-1 mt-4 rounded-full overflow-hidden">
                    <div 
                      className="bg-teal-500 h-full transition-all duration-300 ease-out" 
                      style={{ width: `${(formStep / 3) * 100}%` }}
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  {formStep === 1 && (
                    <div className="space-y-4 animate-in slide-in-from-right-4 duration-300">
                      <div>
                        <label className="text-[9px] uppercase font-bold tracking-[1.5px] text-teal-500 mb-1 flex items-center gap-1"><UserIcon size={9}/> Nome Completo</label>
                        <input 
                          type="text" 
                          placeholder="Ex: João da Silva" 
                          value={formData.name} 
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          className="w-full border border-teal-500/20 bg-[#0a0e27]/50 p-3 text-xs rounded tracking-wider focus:outline-none focus:border-teal-400 font-mono text-white"
                        />
                      </div>
                      <div>
                        <label className="text-[9px] uppercase font-bold tracking-[1.5px] text-teal-500 mb-1 flex items-center gap-1"><UserIcon size={9}/> Como quer ser chamado? (Apelido)</label>
                        <input 
                          type="text" 
                          placeholder="Ex: João" 
                          value={formData.nickname} 
                          onChange={(e) => setFormData({...formData, nickname: e.target.value})}
                          className="w-full border border-teal-500/20 bg-[#0a0e27]/50 p-3 text-xs rounded tracking-wider focus:outline-none focus:border-teal-400 font-mono text-white"
                        />
                      </div>
                      <button 
                        onClick={() => {
                          if (formData.name.trim() && formData.nickname.trim()) setFormStep(2);
                          else setError('Preencha os campos para continuar.');
                        }}
                        className="w-full bg-teal-500 hover:bg-teal-400 p-3 rounded font-bold text-black text-xs tracking-widest transition uppercase mt-4"
                      >
                        Próxima Etapa
                      </button>
                    </div>
                  )}

                  {formStep === 2 && (
                    <div className="space-y-4 animate-in slide-in-from-right-4 duration-300">
                      <div>
                        <label className="text-[9px] uppercase font-bold tracking-[1.5px] text-teal-500 mb-1 flex items-center gap-1"><Phone size={9}/> Telefone / WhatsApp</label>
                        <input 
                          type="tel" 
                          placeholder="DDD + Número" 
                          value={formData.phone} 
                          onChange={(e) => setFormData({...formData, phone: e.target.value})}
                          className="w-full border border-teal-500/20 bg-[#0a0e27]/50 p-3 text-xs rounded tracking-wider focus:outline-none focus:border-teal-400 font-mono text-white"
                        />
                      </div>
                      <div>
                        <label className="text-[9px] uppercase font-bold tracking-[1.5px] text-teal-500 mb-1 flex items-center gap-1"><FileText size={9}/> CPF</label>
                        <input 
                          type="text" 
                          placeholder="Apenas números" 
                          value={formData.cpf} 
                          onChange={(e) => setFormData({...formData, cpf: e.target.value})}
                          className="w-full border border-teal-500/20 bg-[#0a0e27]/50 p-3 text-xs rounded tracking-wider focus:outline-none focus:border-teal-400 font-mono text-white"
                        />
                      </div>
                      <div className="flex gap-2 mt-4">
                        <button 
                          onClick={() => setFormStep(1)}
                          className="w-1/3 bg-white/5 hover:bg-white/10 border border-white/10 p-3 rounded font-bold text-white text-xs tracking-widest transition uppercase"
                        >
                          Voltar
                        </button>
                        <button 
                          onClick={() => {
                            if (formData.phone.trim() && formData.cpf.trim()) setFormStep(3);
                            else setError('Preencha os campos para continuar.');
                          }}
                          className="w-2/3 bg-teal-500 hover:bg-teal-400 p-3 rounded font-bold text-black text-xs tracking-widest transition uppercase"
                        >
                          Próxima Etapa
                        </button>
                      </div>
                    </div>
                  )}

                  {formStep === 3 && (
                    <div className="space-y-4 animate-in slide-in-from-right-4 duration-300">
                      <div>
                        <label className="text-[9px] uppercase font-bold tracking-[1.5px] text-teal-500 mb-1 flex items-center gap-1">💰 Capital para Operar</label>
                        <select 
                          value={formData.capital} 
                          onChange={(e) => setFormData({...formData, capital: e.target.value})}
                          className="w-full border border-teal-500/20 bg-[#0a0e27]/50 p-3 text-xs rounded tracking-wider focus:outline-none focus:border-teal-400 font-mono text-white"
                        >
                          <option value="" disabled>Selecione seu capital</option>
                          <option value="Até R$ 10.000">Até R$ 10.000</option>
                          <option value="De R$ 10.000 a R$ 50.000">De R$ 10.000 a R$ 50.000</option>
                          <option value="De R$ 50.000 a R$ 200.000">De R$ 50.000 a R$ 200.000</option>
                          <option value="Acima de R$ 200.000">Acima de R$ 200.000</option>
                        </select>
                      </div>

                      <div>
                        <label className="text-[9px] uppercase font-bold tracking-[1.5px] text-teal-500 mb-1 flex items-center gap-1">📊 Experiência no Mercado</label>
                        <select 
                          value={formData.experience} 
                          onChange={(e) => setFormData({...formData, experience: e.target.value})}
                          className="w-full border border-teal-500/20 bg-[#0a0e27]/50 p-3 text-xs rounded tracking-wider focus:outline-none focus:border-teal-400 font-mono text-white"
                        >
                          <option value="" disabled>Selecione sua experiência</option>
                          <option value="Iniciante">Iniciante (menos de 1 ano)</option>
                          <option value="Intermediário">Intermediário (1 a 3 anos)</option>
                          <option value="Avançado">Avançado (mais de 3 anos)</option>
                          <option value="Profissional">Profissional / Institucional</option>
                        </select>
                      </div>

                      <div>
                        <label className="text-[9px] uppercase font-bold tracking-[1.5px] text-teal-500 mb-1 flex items-center gap-1">Motivação</label>
                        <textarea 
                          placeholder="Por que você quer ser um dos primeiros a usar o nosso gráfico?" 
                          value={formData.reason} 
                          onChange={(e) => setFormData({...formData, reason: e.target.value})}
                          className="w-full border border-teal-500/20 bg-[#0a0e27]/50 p-3 text-xs rounded tracking-wider focus:outline-none focus:border-teal-400 font-mono text-white min-h-[80px]"
                        />
                      </div>
                      
                      <div className="space-y-2 pt-1">
                        <ConsentSwitch checked={isTermsAccepted} onChange={setIsTermsAccepted}>
                          Li e concordo com os Termos de Uso.
                        </ConsentSwitch>
                        <ConsentSwitch checked={optInMarketing} onChange={setOptInMarketing}>
                          Aceito receber novidades e relatórios da FLOWAES Intelligence. (Opcional)
                        </ConsentSwitch>
                      </div>
                      
                      <div className="flex gap-2 mt-4">
                        <button 
                          onClick={() => setFormStep(2)}
                          className="w-1/3 bg-white/5 hover:bg-white/10 border border-white/10 p-3 rounded font-bold text-white text-xs tracking-widest transition uppercase"
                        >
                          Voltar
                        </button>
                        <button 
                          onClick={handleSaveProfile} 
                          disabled={!isTermsAccepted || isLoading}
                          className={`w-2/3 p-3 rounded font-bold text-xs tracking-widest transition shadow-lg uppercase ${isTermsAccepted ? 'bg-teal-500 text-black hover:bg-teal-400 shadow-[0_0_15px_rgba(20,184,166,0.2)]' : 'bg-gray-800 text-white/30 cursor-not-allowed'}`}
                        >
                          Concluir
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="pt-4 flex justify-center">
                  <button 
                    onClick={() => setStep(5)}
                    className="w-full bg-white/5 hover:bg-white/10 border border-white/10 p-3 rounded font-bold text-white/50 hover:text-white/80 text-xs tracking-widest transition uppercase mt-4"
                  >
                    Voltar ao Painel de Segurança
                  </button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-6 w-full max-w-sm mx-auto">
                <h2 className="text-xl font-bold tracking-widest text-center text-teal-400">TERMO DE SIGILO & NDA</h2>
                
                <div className="h-48 overflow-y-scroll border border-teal-500/20 bg-[#0a0e27]/50 p-4 rounded text-xs leading-relaxed text-white/70 scrollbar-thin scrollbar-thumb-teal-500/20">
                  <p className="font-bold mb-2 text-teal-400">ACORDO DE NÃO-DIVULGAÇÃO E CONFIDENCIALIDADE (NDA)</p>
                  <p>Ao acessar esta área restrita do FLOWAES, você concorda expressamente que os algoritmos, a visualização de fase tridimensional, a cinemática de trajetórias espaciais e a topologia das mola financeiras exibidas constituem segredos industriais protegidos sob sigilo comercial estrito.</p>
                  <p className="mt-4">É terminantemente proibido realizar capturas de tela, gravações de vídeo ou expor publicamente a mecânica de representação do sistema. O descumprimento resultará no cancelamento imediato e definitivo da sua licença sem direito a reembolso, além de sanções civis e perdas e danos multimilionários em juízo.</p>
                </div>
                
                <ConsentSwitch checked={isNdaAccepted} onChange={setIsNdaAccepted}>
                  Eu aceito o Termo de Sigilo.
                </ConsentSwitch>

                <button 
                  onClick={acceptNda} 
                  disabled={!isNdaAccepted || isLoading}
                  className={`w-full p-3 rounded font-bold text-sm tracking-widest transition ${isNdaAccepted ? 'bg-teal-500 text-black hover:bg-teal-400 shadow-[0_0_15px_rgba(20,184,166,0.4)]' : 'bg-gray-800 text-white/30 cursor-not-allowed'}`}
                >
                  INICIAR SESSÃO
                </button>


              </div>
            )}

            {step === 4 && (
              <div className="space-y-6 w-full max-w-sm mx-auto text-center animate-in fade-in zoom-in-95 duration-300">
                {error && error.includes('bloqueada') ? (
                  <>
                    <div 
                      className="w-16 h-16 text-red-500 mx-auto mb-4 flex items-center justify-center bg-red-500/10 rounded-full border border-red-500/30 shadow-[0_0_15px_rgba(239,68,68,0.15)] animate-pulse"
                    >
                      <Lock size={32} />
                    </div>
                    <h2 className="text-xl font-bold tracking-widest text-red-500 uppercase">Acesso Bloqueado</h2>
                    <p className="text-xs text-white/70 leading-relaxed font-mono">
                      Esta conta de usuário foi bloqueada temporariamente ou permanentemente pelo Administrador do sistema por motivos de conformidade ou segurança.
                    </p>

                    <p className="text-[10px] text-white/40 font-mono mb-4 uppercase">
                      Conta: {currentUser?.email || email || 'Desconhecida'}
                    </p>
                    {isAdminUser(auth.currentUser) ? (
                      <div className="mt-6 p-4 border border-teal-500/20 bg-teal-950/10 rounded-lg backdrop-blur-sm animate-in fade-in slide-in-from-bottom duration-300">
                        <p className="text-[10px] tracking-widest text-teal-400 mb-3 font-bold font-mono uppercase flex justify-center items-center gap-1.5">
                          <ShieldCheck size={14} className="text-teal-400" /> LIBERAÇÃO DE ACESSO GOD (PIN)
                        </p>
                        <div className="flex justify-center gap-3 mb-4">
                          {pin.map((digit, idx) => (
                            <input
                              key={idx}
                              id={`pin-input-${idx}`}
                              type="text"
                              inputMode="numeric"
                              maxLength={1}
                              value={digit}
                              onChange={(e) => handlePinChange(idx, e.target.value)}
                              onKeyDown={(e) => handlePinKeyDown(idx, e)}
                              className="w-12 h-12 text-center text-lg font-bold font-mono text-[#00ffa3] bg-[#02040a] border border-[#00ffa3]/30 rounded focus:outline-none focus:border-[#00ffa3] shadow-[0_0_10px_rgba(0,255,163,0.1)] transition"
                            />
                          ))}
                        </div>
                        {pinError && (
                          <p className="text-[10px] text-red-400 font-mono mb-3 uppercase tracking-wider">{pinError}</p>
                        )}
                        <button
                          onClick={handleVerifyPin}
                          disabled={isLoading}
                          className="w-full bg-[#00ffa3]/10 border border-[#00ffa3]/30 p-2.5 rounded font-bold text-[#00ffa3] text-xs tracking-widest hover:bg-[#00ffa3]/20 transition font-mono uppercase"
                        >
                          {isLoading ? 'DESTRAVANDO...' : 'DESBLOQUEAR ACESSO GOD'}
                        </button>
                      </div>
                    ) : (
                      <div className="pt-4 border-t border-white/10 text-[9px] text-white/40 uppercase tracking-widest font-mono">
                        Suporte: support@flowaes.com
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="w-16 h-16 border-t-2 border-teal-500 border-solid rounded-full animate-spin mx-auto mb-4"></div>
                    <h2 className="text-xl font-bold tracking-widest text-teal-400">NA FILA DE ESPERA</h2>
                    <p className="text-sm text-white/70 leading-relaxed font-mono">
                      Recebemos seus dados. Como o ambiente é restrito, estamos processando as aprovações em lotes. Você será notificado quando seu acesso for liberado.
                    </p>
                    
                    <div className="mt-8 p-4 border border-teal-900/50 bg-teal-950/20 rounded-lg backdrop-blur-sm">
                      <p className="text-xs tracking-widest text-white/50 mb-4 font-bold flex justify-center items-center gap-2"><Lock size={12}/> JÁ É UM FUNDADOR?</p>
                      <input 
                        type="text" 
                        placeholder="CHAVE DE ACESSO" 
                        value={activationKey} 
                        onChange={(e) => setActivationKey(e.target.value.toUpperCase())}
                        className="w-full border border-teal-500/30 bg-[#0a0e27]/80 p-3 rounded text-sm tracking-wider focus:outline-none focus:border-teal-400 text-center font-mono mb-4"
                      />
                      <button 
                        onClick={handleActivateKey} 
                        className="w-full bg-transparent border border-teal-500/50 p-2 rounded font-bold text-teal-400 text-xs tracking-widest hover:bg-teal-500/10 transition"
                      >
                        ATIVAR LICENÇA
                      </button>
                    </div>
                  </>
                )}
                
                <button 
                  onClick={async () => {
                    await auth.signOut();
                    setStep(0);
                  }}
                  className="w-full bg-[#ff3d67]/10 border border-[#ff3d67]/30 p-3 rounded font-bold text-[#ff3d67] text-sm tracking-widest hover:bg-[#ff3d67]/20 transition mt-2"
                >
                  SAIR DA CONTA
                </button>
              </div>
            )}

            {step === 5 && (
              <div className="space-y-6 w-full max-w-sm mx-auto text-center">
                <ShieldCheck className="w-12 h-12 text-teal-400 mx-auto opacity-80" />
                <div>
                  <h2 className="text-xl font-bold tracking-widest text-teal-400 uppercase">PAINEL RNA DE SEGURANÇA</h2>
                  <p className="text-[10px] text-white/50 mt-1 font-mono uppercase">
                    Área de ativação de licenças criptográficas FLOWAES.
                  </p>
                </div>

                <div className="p-4 border border-teal-500/20 bg-[#0a0e27]/50 rounded-lg backdrop-blur-sm space-y-4">
                  <div>
                    <label className="text-[9px] uppercase font-bold tracking-[2px] text-teal-500 mb-2 block font-mono text-left"><Lock size={10} className="inline mr-1"/> Insira sua chave de licença</label>
                    <input 
                      type="text" 
                      placeholder="FLOWAES-XXXX-XXXX" 
                      value={activationKey} 
                      onChange={(e) => setActivationKey(e.target.value.toUpperCase())}
                      className="w-full border border-teal-500/30 bg-[#02040a] p-3 rounded text-sm tracking-widest focus:outline-none focus:border-teal-400 text-center font-mono text-white placeholder:text-gray-800"
                    />
                  </div>
                  <button 
                    onClick={handleActivateKey} 
                    className="w-full bg-teal-500 hover:bg-teal-400 p-3 rounded font-bold text-black text-xs tracking-widest transition shadow-[0_0_15px_rgba(20,184,166,0.3)] uppercase"
                  >
                    ATIVAR LICENÇA DE ACESSO
                  </button>
                </div>

                <div className="flex items-center space-x-2 text-white/20 text-xs font-mono">
                  <div className="flex-1 border-t border-white/10"></div>
                  <span>NÃO POSSUI LICENÇA?</span>
                  <div className="flex-1 border-t border-white/10"></div>
                </div>

                <div className="space-y-2">
                  <button 
                    onClick={() => setStep(2)}
                    className="w-full bg-white/5 hover:bg-white/10 border border-white/10 p-3 rounded font-bold text-white text-xs tracking-widest transition uppercase"
                  >
                    PREENCHER FORMULÁRIO DE QUALIFICAÇÃO
                  </button>
                  <button 
                    onClick={async () => {
                      await auth.signOut();
                      setStep(0);
                    }}
                    className="w-full bg-[#ff3d67]/10 border border-[#ff3d67]/20 p-2.5 rounded font-bold text-[#ff3d67] text-xs tracking-widest hover:bg-[#ff3d67]/20 transition"
                  >
                    SAIR DA CONTA
                  </button>
                </div>
              </div>
            )}
            
            {step === 6 && canCreatePassword && (
              <div className="space-y-6 w-full max-w-sm mx-auto">
                <h2 className="text-xl font-bold tracking-widest text-center text-teal-400 uppercase">
                  CRIAR SENHA DE ACESSO
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="text-[10px] uppercase font-bold tracking-[2px] text-teal-500 mb-1 block">Nova Senha</label>
                    <input 
                      type="password" 
                      value={password} 
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full border border-teal-500/20 bg-[#0a0e27]/50 p-3 rounded text-sm tracking-wider focus:outline-none focus:border-teal-400 font-mono text-white"
                    />
                    <p className="text-[9px] text-white/40 mt-1 uppercase">Mín. 8 caracteres, 1 maiúscula, 1 número, 1 símbolo.</p>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase font-bold tracking-[2px] text-teal-500 mb-1 block">Confirmar Senha</label>
                    <input 
                      type="password" 
                      value={passwordConfirm} 
                      onChange={(e) => setPasswordConfirm(e.target.value)}
                      className="w-full border border-teal-500/20 bg-[#0a0e27]/50 p-3 rounded text-sm tracking-wider focus:outline-none focus:border-teal-400 font-mono text-white"
                    />
                  </div>
                  <ConsentSwitch checked={isRobotVerified} onChange={setIsRobotVerified}>
                    Não sou um robô
                  </ConsentSwitch>
                  <button 
                    onClick={handleFinalizeSignup}
                    className="w-full bg-teal-500 p-3 rounded font-bold text-black text-sm tracking-widest hover:bg-teal-400 transition"
                  >
                    FINALIZAR CADASTRO
                  </button>
                </div>
              </div>
            )}
            </>
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
